import * as React from 'react';
import { memo, useCallback } from 'react';

import { PopupNames } from 'core';
import { useTasks, usePopupData, usePopupStateOperations } from 'core/hooks';
import { TaskCreateModal } from 'pages/_detailShared/TaskBoard/TaskCreateModal';
import { ICreateTaskDraft } from 'pages/_detailShared/TaskBoard/taskBuckets';

/** popupData shape for the palette-dispatched «create task» popup. */
export interface ITaskCreatePopupData {
  studentId?: string;
  groupId?: string;
}

/**
 * Registry wrapper for the shared `TaskCreateModal`, dispatched by the palette
 * via `showPopup(PopupNames.taskCreatePopup, { studentId | groupId })`. Owns
 * `useTasks(scope)` and maps the modal draft onto its `add({ title, description,
 * dueDate, priority })` exactly like `TasksPanel` — no new add path.
 */
export const TaskCreatePopup = memo(() => {
  const data = usePopupData(PopupNames.taskCreatePopup);
  const {
    operations: { hidePopup },
  } = usePopupStateOperations();
  const { add, saving } = useTasks({
    studentId: data?.studentId,
    groupId: data?.groupId,
  });

  const close = useCallback(() => hidePopup(PopupNames.taskCreatePopup), [
    hidePopup,
  ]);

  const onCreate = useCallback(
    (draft: ICreateTaskDraft): Promise<boolean> =>
      add({
        title: draft.title,
        description: draft.description,
        dueDate: draft.dueDate,
        priority: draft.priority,
      }),
    [add],
  );

  if (!data) return null;

  return (
    <TaskCreateModal
      open
      onClose={close}
      onCreate={onCreate}
      adding={saving}
      studentId={data.studentId}
      groupId={data.groupId}
    />
  );
});
